<script setup lang="ts">
import {computed, onBeforeMount, ref} from "vue";
import {useDocumentStore} from "@/stores/documentStore";
import type { DocumentsList } from '@/interfaces/documents'
import { VuePDF, usePDF } from '@tato30/vue-pdf'


const documentsStore = useDocumentStore();
const displayDialog = ref(false);
const documents = ref<DocumentsList>({files:[], folders: []});
const folders = ref([]);
const url = ref("");
const selectedFile = ref("");
const folder = ref("home");
const pdfDef = ref<string | null>(null)
const { pdf, pages } = usePDF(pdfDef)

onBeforeMount(async () => {
  await getList();
})

const getList = async () => {
  documents.value = await documentsStore.getDocuments(folder.value);
}

const filesAndFolders = computed(() => {
  var data = [] as any[];
  if (documents.value){
    if (documents.value.folders) {
      documents.value.folders.forEach((x:any) => {
        data.push({type: "folder", name: x.replace(folder.value + '/', ''), extension: "", id: ""});
      });
    }
    if (documents.value.files) {
      documents.value.files!.forEach((x:any) => {
        data.push({type: "file", name: x.originalName!, extension: x.extension!, id: x.id!});
      })
    }
  }
  return data;
});

const downloadFile = async () => {
  var file = documents.value.files?.find((x:any) => x.id == selectedFile.value);
  if (file != null) {
    await documentsStore.download(file);
  }
}

const showFile = async () => {
  var file = documents.value.files?.find((x:any) => x.id == selectedFile.value);
  if (file != null) {
    url.value = await documentsStore.getDataUrl(file);
    displayDialog.value = true;
    pdfDef.value = url.value

  }
}

const onFileClick = (event: any, id: string) => {
    selectedFile.value = id;
};

const onFolderClick = (event: any, id: string) => {
    folder.value += "/" + id;
    getList();
};

const folderBreadcrumbs = computed(() => {
  var folders = folder.value.split("/");
  var output = [];
  var path = "home";
  for (var i = 1; i < folders.length; i++) {
    if (folders[i] == "") continue;
    path += "/" + folders[i];
    output.push({name: folders[i], path: path});
  }
  return output;
})

const changeFolder = (path:string) => {
  folder.value = path;
  getList();
}


</script>

<template>
<div class="p-3 rounded" style="background: #fff; height:100%">
  <div>
    <h1 class="mb-3">Documents</h1>

    <div class="mb-2 ">
      <div class="border border-surface-300 rounded p-1 pl-2 mr-4 mb-2" style="min-width:100%">
        <i class="fa fa-home cursor-pointer" @click="folder = 'home'; getList()" title="Home"></i> 
        <template v-for="f in folderBreadcrumbs" v-bind="f">
          <span class="mx-2">/</span>
          <span class="cursor-pointer text-sm"  @click="changeFolder(f.path)">{{ f.name }}</span>
        </template>
      </div>
      <div class="flex items-center mt-2">
        <el-button
          style="width:50%"
          :disabled="selectedFile == ''" 
          @click="downloadFile()"
          title="Download"
        >
        <i class="fa fa-download cursor-pointer mr-3"></i>
          Download
        </el-button>
        <el-button
          style="width:50%"
          :disabled="selectedFile == '' "
          @click="showFile()" 
          title="View"
        >
        <i class="fa fa-eye cursor-pointer mr-3"></i>
          Preview
        </el-button>
      </div>
    </div>
    <div class="flex gap-2 grid-cols-2 mt-2 flex-wrap">
      <div v-for="file in filesAndFolders" class="rounded" :class="{selected: selectedFile === file.id && file.type=='file'}" :key="file.id+file.name" :title="file.name" @click="onFileClick($event, file.id!)">
        <div class="file text-center hover:bg-surface-200 p-2 rounded cursor-pointer"  v-if="file.type=='file'" :title="file.name+file.extension" @dblclick="showFile()" @click="onFileClick($event, file.id!)">
          <div class="p-1 relative">
            <i class="far fa-file fa-4x"></i>
            <span class="absolute w-full text-center top-10 text-xs left-0">{{ file.extension?.replace(".", "") }}</span>
          </div>
          <div class="label overflow-hidden text-nowrap text-ellipsis w-full">
            {{ file.name }}
          </div>
        </div>
        <div class="file text-center hover:bg-surface-200 p-2 rounded cursor-pointer" v-if="file.type=='folder'" :title="file.name" @click="onFolderClick($event, file.name)">
          <div class="p-1 relative" v-if="file.type == 'folder'" :title="file.name">
            <i class="fas fa-folder fa-4x text-orange-300"></i>
          </div>
          <div class="label overflow-hidden text-nowrap  text-ellipsis w-full" :title="file.name">
            {{ file.name }}
          </div>
        </div>
      </div>
    </div>
    <div v-if="filesAndFolders.length == 0" class=" text-center mt-10 w-full">
      No files or folders found
    </div>
  </div>

  <el-dialog v-model="displayDialog" title="File Preview" width="90vw">
    <div class="mx-auto text-center">
      <div v-if="url.indexOf('/pdf') > -1" style="max-height:100vh;overflow-y:scroll;">
        <div v-for="page in pages" :key="page" style="width:100%; margin-top: 10px;">
          <VuePDF  :pdf="pdf" :page="page" fit-parent />
        </div>
      </div>
      <img v-if="url.indexOf('image/') > -1" :src="url" style="max-width:100%; margin: 0 auto;"/>
    </div>
  </el-dialog>
</div>
</template>

<style scoped>
.file .label, .text-sm{ font-size: .8em;}
.selected{background:rgba(0, 76, 218, 0.274);}
.flex {
  display: flex;
  align-items: center;
}
.w-full {width:100%}
.mt-2 {
  margin-top: .5rem;
}
.mx-auto{ margin: 0 auto;}
.mx-2 {
  margin-left: .5rem;
  margin-right: .5rem;
}
.mb-3{margin-bottom: .75rem;}
.mr-4 {
  margin-right: 1rem;
}
.mr-3 {
  margin-right: .75rem;
}
.mt-10{
  margin-top:10rem;
}
.cursor-pointer {
  cursor: pointer;  
}
.p-1 {
    padding: .25rem;
}
.pl-2 {
    padding-left: .5rem;
}

.border-surface-300 {
    border-color: #aaa;
    border-style: solid;
}
.border {
    border-width: 1px;
}
.rounded {
    border-radius: .25rem;
}
.ml-4 {
    margin-left: 1rem;
}
.p-3 {
    padding: .75rem;
}
.bg-surface-0 {
    background-color: #aaa;
}
.rounded-md {
    border-radius: .375rem;
}
.gap-2 {
    gap: .5rem;
}
.justify-between {
    justify-content: space-between;
}
.items-center {
    align-items: center;
}
.flex-wrap {
    flex-wrap: wrap;
}
.text-center{
    text-align: center;
}
.text-orange-300 {
    --tw-text-opacity: 1;
    color: rgb(253 186 116);
}
.grid-cols-2 > div{
  min-width:31%;
  width:31%;
  padding: 10px;
}
.relative{
  position:relative
}
.absolute{
  position:absolute
}
.text-nowrap {
    text-wrap: nowrap;
}
.text-ellipsis {
    text-overflow: ellipsis;
}
.overflow-hidden {
    overflow: hidden;
}
.w-full {
    width: 100%;
}
.text-xs {
    font-size: 0.75rem;
    line-height: 1rem;
}
.text-center {
    text-align: center;
}
.top-10 {
    top: 2.5rem;
}
.left-0 {
    left: 0px;
}
@media screen and (max-width: 400px) {
  .grid-cols-2 > div{
    min-width:48%;
    width:48%;
  }
}
</style>